import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import type { ProductAspect } from '../../kpf/entities/aspects/ProductAspect.js';
import type { ChancenwerkUserAspect } from '../../users/entities/aspects/ChancenwerkUserAspect.js';
import type { PartnerActiveAspect } from '../entities/aspects/PartnerActiveAspect.js';
import type { PartnerAspect } from '../entities/aspects/PartnerAspect.js';
import { Partner } from '../entities/Partner.js';

export enum PartnerActiveState {
  YES = 'ja',
  NO = 'nein',
  ACQUISITION = 'In Akquise',
}

@Entity()
export class PartnerFilter extends AdornisFilter {
  static override _class = 'PartnerFilter';

  constructor(doc: PartialEntityData<PartnerFilter>) {
    super(doc);
  }

  // Only used to check if a filter has been changed to a new one
  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => [String])
  public ids: Maybe<string[]>;

  @Field(() => [String])
  public partnerClass: Maybe<string[]>;

  protected getFilterArray() {
    const filter = this.toObject();

    // Wen want to have full kuerzel search. Even on products so we just ignore the product
    let kuerzelSchool = '';
    if (filter.search) {
      kuerzelSchool = filter.search.split('_').slice(0, 2).join('_');
    }

    const filterClass = filter.partnerClass?.length ? { _class: { $in: filter.partnerClass } } : {};
    const filterIds = filter.ids?.length ? { _id: { $in: filter.ids } } : {};

    const filterName = filter.search
      ? {
          $or: [
            { name: { $regex: this._safeRegex(filter.search), $options: 'i' } },
            { kuerzel: { $regex: this._safeRegex(kuerzelSchool), $options: 'i' } },
            { addressFullText: { $regex: this._safeRegex(filter.search), $options: 'i' } },
          ],
        }
      : {};

    return [filterClass, filterIds, filterName];
  }

  override getMongoFilter(): Dictionary<any> {
    const filters = this.getFilterArray();
    const extendedFilter = super.getMongoFilter();
    if (extendedFilter?.$and?.length) filters.push(extendedFilter);
    if (!filters.length) return {};
    return { $and: filters };
  }
}

/** Field name of the dedicated sort key materialized for the `productStrings` column. */
const PRODUCT_STRINGS_SORT_KEY = '_productStringsSortKey';

/**
 * Aggregation stages that materialize a sort key for each Partner so that
 * sorting by the `productStrings` column groups partners with identical product
 * combinations together. Mirrors the resolver of {@link Partner.productStrings}:
 * last 3 characters of each KPF's `produkt._class` concatenated with last 3
 * characters of each `ragProducts._class`.
 *
 * The values are sorted alphabetically and joined into a single string with a
 * `|` delimiter, so MongoDB compares them element-wise as a lexicographic
 * string (not as an array, which would only compare the min/max element).
 *
 * Only needed when the filter actually sorts by `productStrings`.
 */
const productStringsSortStages = () => {
  const last3Chars = (strExpr: any) => ({
    $let: {
      vars: { s: { $ifNull: [strExpr, ''] } },
      in: { $substrCP: ['$$s', { $max: [0, { $subtract: [{ $strLenCP: '$$s' }, 3] }] }, 3] },
    },
  });
  return [
    { $lookup: { from: 'kpfs', localField: '_id', foreignField: 'schoolID', as: '_productStringsKpfs' } },
    {
      $addFields: {
        [PRODUCT_STRINGS_SORT_KEY]: {
          $reduce: {
            input: {
              $sortArray: {
                input: {
                  $concatArrays: [
                    {
                      $map: {
                        input: { $ifNull: ['$_productStringsKpfs', []] },
                        as: 'kpf',
                        in: last3Chars('$$kpf.produkt._class'),
                      },
                    },
                    { $map: { input: { $ifNull: ['$ragProducts', []] }, as: 'rag', in: last3Chars('$$rag._class') } },
                  ],
                },
                sortBy: 1,
              },
            },
            initialValue: '',
            in: { $concat: ['$$value', '|', '$$this'] },
          },
        },
      },
    },
    { $unset: '_productStringsKpfs' },
  ];
};

export const searchPartnerAggregation = (filter: PartnerFilter) => {
  let activeMatch = {};
  let activeKPFMatch = {};

  const activeAspect = filter.getFilterAspect<PartnerActiveAspect>('partnerActiveState');
  const connectedAspect = filter.getFilterAspect<ChancenwerkUserAspect>('partnerConnectedPerson');
  const fundingPartner = filter.getFilterAspect<PartnerAspect>('partnerFunding');
  const productAspect = filter.getFilterAspect<ProductAspect>('productStrings');

  const filterSocialIndicatorsAspect = filter.getFilterAspect<IntAspect>('socialIndicatorSchema');

  switch (activeAspect?.value) {
    case PartnerActiveState.YES:
      activeMatch = {
        $or: [
          { 'kpfs._id': { $exists: true } },
          {
            $and: [
              {
                $or: [
                  { endDate: { $exists: false } },
                  { endDate: null },
                  { endDate: undefined },
                  { endDate: { $gt: new Date() } },
                ],
              },
              { startDate: { $lte: new Date() } },
            ],
          },
        ],
      };

      // * If we search for active partners we want to have all active kpfs,
      // * because a partner can be active if at least one kpf is active
      activeKPFMatch = {
        startDatum: { $lte: new Date() },
        $or: [
          { endDatum: { $exists: false } },
          { endDatum: null },
          { endDatum: undefined },
          { endDatum: { $gt: new Date() } },
        ],
      };
      break;
    case PartnerActiveState.NO:
      activeMatch = { endDate: { $lt: DateTime.now().toJSDate() } };
      break;
    case PartnerActiveState.ACQUISITION:
      activeMatch = {
        _class: { $in: ['ChancenSchule', 'School'] },
        $and: [
          {
            $nor: [
              { 'kpfs._id': { $exists: true } },
              {
                $and: [
                  {
                    $or: [
                      { endDate: { $exists: false } },
                      { endDate: null },
                      { endDate: undefined },
                      { endDate: { $gt: new Date() } },
                    ],
                  },
                  { startDate: { $lte: new Date() } },
                ],
              },
            ],
          },
        ],
      };

      activeKPFMatch = { startDatum: { $exists: true } };
      break;
  }

  return [
    ...(activeAspect?.value || connectedAspect?.value || fundingPartner?.value?.length || productAspect?.values?.length
      ? [
          {
            $lookup: {
              from: 'kpfs',
              let: { partnerID: '$_id' },
              pipeline: [{ $match: { $expr: { $eq: ['$schoolID', '$$partnerID'] }, ...activeKPFMatch } }],
              as: 'kpfs',
            },
          },
          { $unwind: { path: '$kpfs', preserveNullAndEmptyArrays: true } },
        ]
      : []),
    { $match: activeMatch },
    ...(fundingPartner?.value?.length
      ? [{ $match: { 'kpfs.ragPartnerInformation.ragPartnerIsID': { $in: fundingPartner?.value } } }]
      : []),
    ...(productAspect?.values?.length ? [{ $match: { 'kpfs.produkt._class': { $in: productAspect?.values } } }] : []),
    ...(connectedAspect?.value
      ? [
          {
            $lookup: {
              from: SemanticRelationship._collectionName,
              let: { kpfIDs: '$kpfs._id', partnerID: '$_id' },
              pipeline: [
                {
                  $match: {
                    $expr: { $or: [{ $eq: ['$objectID', '$$kpfIDs'] }, { $eq: ['$objectID', '$$partnerID'] }] },
                    $and: [connectedAspect?.value ? { subjectID: connectedAspect?.value } : {}],
                  },
                },
              ],
              as: 'relations',
            },
          },
          { $match: { $or: [connectedAspect?.value ? { $or: [{ relations: { $not: { $size: 0 } } }] } : {}] } },
        ]
      : []),
    {
      $addFields: {
        addressFullText: { $concat: ['$address.zipCity.zip', '$address.zipCity.city', ', ', '$address.strasse'] },
      },
    },
    ...(filterSocialIndicatorsAspect &&
    (filterSocialIndicatorsAspect?.max !== null || filterSocialIndicatorsAspect?.min !== null)
      ? [
          { $addFields: { indicatorsSort: { $sortArray: { input: '$socialIndicators', sortBy: { value: -1 } } } } },
          { $addFields: { latestObject: { $arrayElemAt: ['$indicatorsSort', 0] } } },
          {
            $match: {
              'latestObject.value': {
                $gte: filterSocialIndicatorsAspect?.min ?? 0,
                $lte: filterSocialIndicatorsAspect?.max ?? 9999, // arbitrary high number. Socialindices will not go this high
              },
            },
          },
        ]
      : []),

    { $match: filter.getMongoFilter() },

    // dedupe ?
    { $group: { _id: '$_id', doc: { $first: '$$ROOT' } } },
    { $replaceRoot: { newRoot: '$doc' } },
  ];
};

const resolveSubscribePartnerByFilter =
  (skip: number, limit: number, filter: PartnerFilter) => (gqlFields: BaseQLSelectionSet<Partner>) => {
    return watchEntireCollection(Partner._collectionName).pipe(
      switchMap(async () => {
        const pf = await publishFilter(Partner._class);
        const collection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
        const originalSort = filter.getMongoOptions().sort;
        const sortsOnProductStrings = 'productStrings' in originalSort;
        // Remap `productStrings` to the materialized string sort key so
        // partners with identical product combinations are grouped together.
        const sort: Record<string, any> = {};
        for (const [key, dir] of Object.entries(originalSort)) {
          sort[key === 'productStrings' ? PRODUCT_STRINGS_SORT_KEY : key] = dir;
        }

        const result = await collection
          .aggregate([
            ...(pf ? [{ $match: pf }] : []),
            ...searchPartnerAggregation(filter),
            ...(sortsOnProductStrings ? productStringsSortStages() : []),
            { $sort: sort },
            ...(sortsOnProductStrings ? [{ $unset: PRODUCT_STRINGS_SORT_KEY }] : []),
            { $skip: skip },
            { $limit: limit },
          ])
          .toArray();

        return result;
      }),
    );
  };

const resolveSubscribeCountPartnerByFilter = (filter: PartnerFilter) => () => {
  return from(watchEntireCollection(Partner._collectionName)).pipe(
    switchMap(async docs => {
      const pf = await publishFilter(Partner._class);
      const collection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
      const result = await collection
        .aggregate([...(pf ? [{ $match: pf }] : []), ...searchPartnerAggregation(filter), { $count: 'count' }])
        .toArray();

      return result[0]?.count ?? 0;
    }),
  );
};

export const subscribePartnerByFilter = registerSubscription({
  type: () => [Partner],
  operationName: 'subscribePartnerByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => PartnerFilter, name: 'filter' },
  ],
  resolve: resolveSubscribePartnerByFilter,
});

export const subscribeCountPartnerByFilter = registerSubscription({
  type: () => Int,
  operationName: 'subscribeCountPartnerByFilter',
  params: [{ type: () => PartnerFilter, name: 'filter' }],
  resolve: resolveSubscribeCountPartnerByFilter,
});
